import { Card } from "@/components/ui/card";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Clock, Eye, Calendar, Sparkles, User, GitFork, ExternalLink, Flame, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { formatDuration, formatDateTime } from "@/lib/format";
import { getProjectStatusLabel } from "@/constants/project-status";
import { MyLibrary } from "@/generated/graphql";
import CampaignBudgetBar from "@/components/CampaignBudgetBar";
import { ProcessingStepper } from "@/components/ProcessingStepper";
import { OriginalVideoDownloadButton } from "@/components/editor/OriginalVideoDownloadButton";
import ProFeatureDialog from "@/components/ProFeatureDialog";
import { resolveOriginalVideoDownloadUrl } from "@/lib/downloads/resolveOriginalVideoDownloadUrl";
import { useHasFeature } from "@/lib/useHasFeature";
import { useCallback, useMemo, useState } from "react";

type RemixWindow = '24h' | '7d' | '30d' | '90d';

const REMIX_WINDOW_I18N_KEY: Record<RemixWindow, string> = {
  '24h': 'communityLibrary.remixesInWindow24h',
  '7d': 'communityLibrary.remixesInWindow7d',
  '30d': 'communityLibrary.remixesInWindow30d',
  '90d': 'communityLibrary.remixesInWindow90d',
};

interface CommunityLibraryCardProps {
  library: MyLibrary;
  onGenerateClips: (library: MyLibrary) => void;
  // When set, the card appends "(N <window-label>)" after the all-time count.
  // Pass null/undefined when sorting by anything other than a windowed remix sort.
  activeRemixWindow?: RemixWindow | null;
}

const CommunityLibraryCard = ({ library, onGenerateClips, activeRemixWindow }: CommunityLibraryCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasProAccess = useHasFeature('pro_features');
  const [thumbnailError, setThumbnailError] = useState(false);
  const [proDownloadOpen, setProDownloadOpen] = useState(false);

  const handleThumbnailError = useCallback(() => {
    setThumbnailError(true);
  }, []);

  const durationLabel = formatDuration(library.duration_seconds);
  const dateLabel = formatDateTime(library.createdAt);
  const thumbnail = library.thumbnail_url || undefined;
  const youtubeChannels = library.youtube_channels || [];
  const primaryChannel = youtubeChannels[0] || null;
  const channelName = primaryChannel?.name || null;
  const channelThumbnail = primaryChannel?.thumbnail || null;
  const uploaderName = (library as any).created_by_user_name || null;
  const viewCount = Number((library as any).view_count) || 0;
  const uploadDate = (library as any).upload_date || null;
  const remixCount = (library as any).remix_count ?? 0;
  const windowedRemixCount: number | null = (library as any).windowed_remix_count ?? null;
  const showWindowedRemixCount = activeRemixWindow != null && windowedRemixCount != null;
  const isProcessing = !['success', 'failure', 'expired'].includes(library.curation_status);

  const formatViewCount = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return String(count);
  };

  // Format YYYYMMDD to localized date, fall back to createdAt
  const displayDate = useMemo(() => {
    if (uploadDate && /^\d{8}$/.test(uploadDate)) {
      const y = uploadDate.slice(0, 4);
      const m = uploadDate.slice(4, 6);
      const d = uploadDate.slice(6, 8);
      return formatDateTime(`${y}-${m}-${d}`);
    }
    return dateLabel;
  }, [uploadDate, dateLabel]);

  interface ActiveCampaign {
    id: string;
    name: string;
    cpm_rate_cents: number;
    currency: string;
    budget_total_cents: number;
    budget_remaining_cents: number;
    budget_pending_cents: number;
    invite_only?: boolean;
    my_approved_platforms?: string[];
  }

  const campaigns: ActiveCampaign[] = (library as any).active_cpm_campaigns || [];
  const primaryCampaign = campaigns[0] || null;
  const campaignCount = campaigns.length;
  const hasCampaign = campaignCount > 0;
  const isInviteOnlyLocked =
    !!primaryCampaign?.invite_only &&
    (primaryCampaign?.my_approved_platforms?.length ?? 0) === 0;
  const isNavigableCampaign =
    !isProcessing && hasCampaign && !!primaryCampaign && !isInviteOnlyLocked;
  const canGenerateFromSurface = !isProcessing && !hasCampaign;
  const hasSurfaceAction = isNavigableCampaign || canGenerateFromSurface;
  const originalDownloadUrl = resolveOriginalVideoDownloadUrl({
    originalVideoUrl: library.original_video_url,
    cutVideoUrl: library.cut_video_url,
    baseVideoUrl: library.base_video_url,
    compressedBaseVideoUrl: library.compressed_base_video_url,
  });
  const cardActionLabel = `${t('library.generateAIClips')}: ${library.name}`;
  const interactiveClasses = hasSurfaceAction
    ? "cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
    : "transition-colors duration-300";

  const cardBody = (
    <Card
      className={
        hasCampaign
          ? `relative overflow-hidden p-0 group border border-emerald-500/40 bg-gradient-to-b from-emerald-500/10 via-card to-card ${interactiveClasses} ${hasSurfaceAction ? "hover:shadow-[0_0_24px_rgba(16,185,129,0.25)]" : ""}`
          : `relative overflow-hidden p-0 group border-border/50 ${interactiveClasses} ${hasSurfaceAction ? "hover:shadow-glow" : ""}`
      }
    >
      {canGenerateFromSurface && (
        <button
          type="button"
          aria-label={cardActionLabel}
          className="absolute inset-0 z-10 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={() => onGenerateClips(library)}
        />
      )}
      {isNavigableCampaign && primaryCampaign && (
        <Link
          to={`/campaigns/${primaryCampaign.id}`}
          aria-label={`${t('communityLibrary.seeCampaign')}: ${primaryCampaign.name}`}
          className="absolute inset-0 z-10 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        />
      )}

      {/* CPM campaign ribbon — matches the channel hero card visual identity */}
      {hasCampaign && (
        <div className={isInviteOnlyLocked
          ? "bg-gradient-to-r from-muted/60 via-muted/30 to-transparent border-b border-border/40"
          : "bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border-b border-primary/30"
        }>
          <div className="px-3 py-1.5 flex items-center justify-center gap-1.5">
            {isInviteOnlyLocked
              ? <Lock className="w-3.5 h-3.5 text-muted-foreground" />
              : <Flame className="w-3.5 h-3.5 text-primary" />
            }
            <div className="flex flex-col items-center">
              <span className={`text-[11px] font-bold tracking-wide ${isInviteOnlyLocked ? "text-muted-foreground" : "text-primary"}`}>
                {isInviteOnlyLocked
                  ? t('campaign.inviteOnly', 'Campanha privada')
                  : campaignCount > 1
                    ? t('campaign.multipleBadge', { count: campaignCount })
                    : t('communityLibrary.receiveToClip')}
              </span>
              {campaignCount === 1 && primaryCampaign?.name && (
                <span className={`text-[10px] leading-tight truncate max-w-[160px] ${isInviteOnlyLocked ? "text-muted-foreground/60" : "text-primary/70"}`}>
                  {primaryCampaign.name}
                </span>
              )}
            </div>
          </div>
          {campaignCount === 1 && primaryCampaign?.budget_total_cents > 0 && (
            <div className="px-3 pb-1.5">
              <CampaignBudgetBar
                totalCents={primaryCampaign.budget_total_cents}
                spentCents={Math.max(0, (primaryCampaign.budget_total_cents || 0) - (primaryCampaign.budget_remaining_cents || 0))}
                pendingCents={primaryCampaign.budget_pending_cents || 0}
                currency={primaryCampaign.currency || 'BRL'}
                size="sm"
              />
            </div>
          )}
        </div>
      )}

      {/* Thumbnail */}
      <div className="aspect-video bg-gradient-secondary overflow-hidden relative">
        {thumbnail && !thumbnailError ? (
          <img
            src={thumbnail}
            alt={library.name}
            className={`w-full h-full object-cover transition-transform duration-300 ${
              hasSurfaceAction ? 'group-hover:scale-105' : ''
            }`}
            onError={handleThumbnailError}
          />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <span className="text-muted-foreground text-xs">{library.name}</span>
          </div>
        )}

        {/* Processing overlay */}
        {isProcessing && (
          <div className="absolute inset-0 flex flex-col justify-end p-2.5 bg-gradient-to-t from-black/85 via-black/40 to-transparent backdrop-blur-[1px] pointer-events-none">
            <ProcessingStepper status={library.curation_status} className="mb-2" />
            <span className="text-white text-xs font-medium leading-tight">{getProjectStatusLabel(library.curation_status)}</span>
          </div>
        )}

        {/* Duration badge */}
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 text-white text-xs font-medium backdrop-blur-sm">
            <Clock className="w-3 h-3" />
            {durationLabel}
          </span>
        </div>

        {/* Channel avatars (stacked for multiple channels) */}
        {youtubeChannels.length > 0 ? (
          <div className="absolute top-2 left-2 flex -space-x-2">
            {youtubeChannels.slice(0, 3).map((ch, idx) => (
              ch.thumbnail ? (
                <img
                  key={ch.channel_id || idx}
                  src={ch.thumbnail}
                  alt={ch.name}
                  className="w-7 h-7 rounded-full border-2 border-white/80 shadow-sm object-cover"
                  style={{ zIndex: youtubeChannels.length - idx }}
                />
              ) : (
                <div
                  key={ch.channel_id || idx}
                  className="w-7 h-7 rounded-full border-2 border-white/80 shadow-sm bg-black/60 flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ zIndex: youtubeChannels.length - idx }}
                >
                  {ch.name?.charAt(0).toUpperCase()}
                </div>
              )
            ))}
            {youtubeChannels.length > 3 && (
              <div className="w-7 h-7 rounded-full border-2 border-white/80 shadow-sm bg-black/60 flex items-center justify-center text-[10px] font-bold text-white">
                +{youtubeChannels.length - 3}
              </div>
            )}
          </div>
        ) : channelName && (
          <div className="absolute top-2 left-2">
            {channelThumbnail ? (
              <img
                src={channelThumbnail}
                alt={channelName}
                className="w-7 h-7 rounded-full border-2 border-white/80 shadow-sm object-cover"
              />
            ) : (
              <div className="w-7 h-7 rounded-full border-2 border-white/80 shadow-sm bg-black/60 flex items-center justify-center text-[10px] font-bold text-white">
                {channelName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        )}

        {/* Action overlay bar */}
        {!isProcessing && (
          <div className="absolute bottom-0 left-0 right-0 flex gap-1.5 items-center px-1.5 pb-1.5 pt-6 bg-gradient-to-t from-black/85 via-black/50 to-transparent">
            <Button
              variant="gradient"
              size="sm"
              className="relative z-20 flex-1 h-7 text-xs font-semibold"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onGenerateClips(library);
              }}
            >
              <Sparkles className="w-3 h-3 mr-1" />
              {t('library.generateAIClips')}
            </Button>
            {originalDownloadUrl && (
              <OriginalVideoDownloadButton
                sourceUrl={originalDownloadUrl}
                hasProAccess={hasProAccess}
                sourceDuration={library.duration_seconds}
                appearance="overlay"
                showExportHint={false}
                i18nPrefix="communityLibrary.originalVideoDownload"
                onProRequired={() => setProDownloadOpen(true)}
              />
            )}
            {library.youtube_video_id && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={`https://www.youtube.com/watch?v=${library.youtube_video_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('communityLibrary.watchOnYouTube')}
                    onClick={(e) => e.stopPropagation()}
                    className="relative z-20 h-8 w-8 md:h-7 md:w-7 flex-shrink-0 inline-flex items-center justify-center rounded-lg bg-gradient-primary text-black font-semibold hover:shadow-glow hover:scale-105 transition-all duration-300"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  {t('communityLibrary.watchOnYouTube')}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        {/* Title */}
        <h3 className={`font-semibold text-sm line-clamp-2 transition-all duration-300 ${
          hasSurfaceAction
            ? 'group-hover:bg-gradient-primary group-hover:bg-clip-text group-hover:text-transparent'
            : ''
        }`}>
          {library.name}
        </h3>

        {/* Channel name(s) + uploader */}
        <div className="space-y-0.5">
          {youtubeChannels.length > 1 ? (
            <p className="text-xs text-muted-foreground truncate">
              {youtubeChannels.map((ch) => ch.name).join(' · ')}
            </p>
          ) : channelName && (
            <p className="text-xs text-muted-foreground truncate">
              {channelName}
            </p>
          )}
          {uploaderName && (
            <p className="text-[11px] text-muted-foreground/70 truncate flex items-center gap-1">
              <User className="w-3 h-3 flex-shrink-0" />
              {t('communityLibrary.uploadedBy', { name: uploaderName })}
            </p>
          )}
        </div>

        {/* Engagement metrics — wrap as whole units; PT labels ("visualizações")
            are too long to cohabit a single line with the date. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {viewCount > 0 && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <Eye className="w-3.5 h-3.5" />
              {formatViewCount(viewCount)} {t('communityLibrary.views')}
            </span>
          )}
          {remixCount > 0 && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <GitFork className="w-3.5 h-3.5" />
              {remixCount} {t('communityLibrary.remixes', { count: remixCount })}
              {showWindowedRemixCount && (
                <span className="text-muted-foreground/70 whitespace-nowrap">
                  {' '}
                  {t(REMIX_WINDOW_I18N_KEY[activeRemixWindow!], { count: windowedRemixCount })}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Date — its own row so it never collides with the metrics above. */}
        <div className="flex items-center text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Calendar className="w-3.5 h-3.5" />
            {displayDate}
          </span>
        </div>

      </div>
    </Card>
  );

  return (
    <>
      {cardBody}
      <ProFeatureDialog
        open={proDownloadOpen}
        onOpenChange={setProDownloadOpen}
        onUpgrade={() => navigate('/plans')}
        title={t('communityLibrary.originalVideoDownload.proTitle')}
        body={t('communityLibrary.originalVideoDownload.proBody')}
        dismissLabel={t('communityLibrary.originalVideoDownload.proDismiss')}
        upgradeLabel={t('communityLibrary.originalVideoDownload.proUpgrade')}
        testIdPrefix="community-original-download-pro"
      />
    </>
  );
};

export default CommunityLibraryCard;
